// src/pages/enterprise-ai/AgentBuilderPage.js
//
// Agent Builder. A sibling surface to Genesis for the unitedDomainAgents fleet.
//
// Three columns:
//   left    the agent list
//   center  the state rail, and nothing else. It is the point of the page.
//   right   the tool and artifact palettes as collapsed drawers, then the
//           inspector for the selected state
//
// THIS PAGE PROPOSES CHANGES. IT NEVER WRITES THEM. There is no save action.
// Every drag and every edit appends to a pending ops list, and the header
// button renders those ops as a Rule 12 surgical $set script you can copy.
// No network call is made anywhere in this file.
//
// WIRE: GET /api/internal/agents (exists, lean only)
//       GET /api/internal/agents/:id/full (must be built, T1)
//       GET /api/internal/agents/tools (must be built, T1)
//       POST /api/admin/agent-builder/migration (later, T6)
// Each of the four is marked again at the exact spot it lands.

import React, { useState, useMemo, useRef } from 'react';
import { Box, Typography, Button, TextField, Tooltip } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BuildIcon from '@mui/icons-material/Build';
import LayersIcon from '@mui/icons-material/Layers';
import ArticleIcon from '@mui/icons-material/Article';
import CloseIcon from '@mui/icons-material/Close';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ScienceIcon from '@mui/icons-material/Science';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { THEME } from './genesisData';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Modal from '../../components/common/Modal';
import { AGENTS, TOOL_CATALOG, ARTIFACT_CATALOG } from './agentBuilderData';
import { issuesForAgent } from './agentBuilderIssues';

// ---------------------------------------------------------------------------
// Copied from GenesisPage.js, consolidate to src/components/common when this page is wired to real data.
// ---------------------------------------------------------------------------

const TONE_MAP = {
  mint: { bg: THEME.mintDim, color: THEME.mint },
  amber: { bg: THEME.amberDim, color: THEME.amber },
  blue: { bg: THEME.blueDim, color: THEME.blue },
  // pink is the one addition: issue counts need a red badge and Genesis had
  // no tone for THEME.pink.
  pink: { bg: 'rgba(198,61,87,0.15)', color: THEME.pink },
  muted: { bg: 'rgba(255,255,255,0.06)', color: THEME.textMuted },
};

const scrollSx = {
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: 'rgba(18,192,153,0.4)', borderRadius: '10px' },
};

const Card = ({ children, sx = {}, raised = false }) => (
  <Box
    sx={{
      background: raised ? THEME.cardRaised : THEME.card,
      border: `1px solid ${THEME.border}`,
      borderRadius: '12px',
      p: 2.5,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const Badge = ({ children, tone = 'muted' }) => {
  const t = TONE_MAP[tone] || TONE_MAP.muted;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        background: t.bg,
        color: t.color,
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        px: '8px',
        py: '3px',
        borderRadius: '4px',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  );
};

const StatusDot = ({ color, size = 8 }) => (
  <Box sx={{ width: size, height: size, borderRadius: '50%', background: color, flexShrink: 0 }} />
);

const SectionHeader = ({ n, label, sublabel, IconComponent, labelTip }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: THEME.mintDim,
        color: THEME.mint,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '13px',
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {n}
    </Box>
    {IconComponent && <IconComponent sx={{ fontSize: 18, color: THEME.textSecondary }} />}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: THEME.textPrimary, lineHeight: 1.2 }}>
          {label}
        </Typography>
        {labelTip && <HintIcon text={labelTip} />}
      </Box>
      {sublabel && <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>{sublabel}</Typography>}
    </Box>
  </Box>
);

const outlinedBtnSx = {
  color: THEME.textSecondary,
  borderColor: THEME.borderStrong,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '13px',
  '&:hover': { borderColor: THEME.mint, color: THEME.mint, background: THEME.mintFaint },
};

const mintBtnSx = {
  background: THEME.mint,
  color: '#06281f',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '13px',
  boxShadow: 'none',
  '&:hover': { background: '#0fa884', boxShadow: 'none' },
};

// ---------------------------------------------------------------------------
// Local constants
// ---------------------------------------------------------------------------

// severity comes off the rule functions as 'error' or 'drift' and is shown to
// the user with those exact words.
const SEVERITY_COLOR = { error: THEME.pink, drift: THEME.amber };

const TIP = {
  agentList:
    'Every agent in the unitedDomainAgents fleet. The issue badge counts the rule failures across that agent states.',
  stateRail:
    'The states of the selected agent in document order. A state with the pause marker halts the turn and waits for the user before it can move on.',
  // Written for someone who has never read the agent document. No field names,
  // no "state", no "artifact", nothing that only makes sense in the code.
  tools:
    "These are the things an agent is allowed to do, like searching flights or issuing a payment card. Drop one on a step to allow it there.",
  artifacts:
    "These are the visuals a person sees in chat, like a flight offer card or a receipt. Drop one on a step to show it at that moment.",
  memoryContract:
    'What the state requires in memory on entry and guarantees in memory on exit. Enforced by T1 at runtime, read only here.',
  pendingOps:
    'Nothing here has been written. Each entry is one surgical $set waiting to be rendered into an update script.',
  issues:
    'Every rule failure on this state, with the reason it matters. Pips under a state chip are the same issues in miniature.',
};

const DND_MIME = 'application/x-agent-builder';

// Monotonic op ids. Date.now() alone collides when two drags land in the same
// millisecond, and React keys on these.
let opSeq = 0;
const nextOpId = () => `op-${++opSeq}`;

const isBuilderDrag = (e) =>
  !!(e.dataTransfer && e.dataTransfer.types && Array.from(e.dataTransfer.types).includes(DND_MIME));

const readDrag = (e) => {
  try {
    return JSON.parse(e.dataTransfer.getData(DND_MIME));
  } catch (err) {
    return null;
  }
};

// Every tooltip on this page is a read-only preview, so none of them may eat
// the pointer. The rail wraps onto multiple rows, which means a preview opened
// from a lower chip routinely covers a chip in the row above; if the popper is
// hit-testable, that upper chip becomes unhoverable and undraggable. Two belts:
// disableInteractive on each Tooltip (MUI then leaves the popper at
// pointerEvents none) and an explicit popper override here, so the behaviour
// does not depend on which MUI version is installed.
const darkTooltipProps = {
  popper: { sx: { pointerEvents: 'none' } },
  tooltip: {
    sx: {
      background: THEME.cardRaised,
      color: THEME.textPrimary,
      fontSize: 11,
      lineHeight: 1.5,
      padding: '8px 10px',
      maxWidth: 280,
      border: `1px solid ${THEME.border}`,
      pointerEvents: 'none',
    },
  },
  arrow: { sx: { color: THEME.cardRaised } },
};

// Long enough that a preview does not flash open while the pointer crosses
// chips during a drag, short enough to still feel like a hover.
const TIP_ENTER_DELAY = 150;

// Shared props for every Tooltip on this page. Spread, do not retype.
const passThroughTip = {
  arrow: true,
  disableInteractive: true,
  enterDelay: TIP_ENTER_DELAY,
  enterNextDelay: TIP_ENTER_DELAY,
  componentsProps: darkTooltipProps,
};

// Local stand-in for the shared common/InfoIcon. Same look, but pointer
// transparent and delayed like everything else here. Kept local so this fix
// stays inside the Agent Builder; lift it into common/InfoIcon.js when these
// copied components consolidate.
const HintIcon = ({ text }) => {
  if (!text) return null;
  return (
    <Tooltip title={text} placement="top" {...passThroughTip}>
      <InfoOutlinedIcon
        sx={{
          fontSize: 12,
          color: THEME.textFaint,
          ml: 0.5,
          cursor: 'help',
          verticalAlign: 'middle',
          '&:hover': { color: THEME.textSecondary },
        }}
      />
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Rule 12 migration script
// ---------------------------------------------------------------------------
//
// WIRE: POST /api/admin/agent-builder/migration (later, T6)
//   Today this function builds the script text in the browser and the tray
//   shows it for copying. Later the same ops array is the request body, T6
//   generates and stores the script server side, and this local builder
//   becomes the offline fallback rather than the source of truth.

const buildMigrationScript = (agent, ops) => {
  const nextVersion = (agent.version || 1) + 1;
  const generated = new Date().toISOString();
  const L = [];

  L.push('// ====================================================================');
  L.push('// Rule 12 surgical migration, proposed by Agent Builder');
  L.push('// ====================================================================');
  L.push('// collection    : unitedDomainAgents');
  L.push('// agentId       : ' + agent.agentId);
  L.push('// version       : ' + agent.version + ' -> ' + nextVersion);
  L.push('// activeVersion : ' + agent.activeVersion + '  (NOT written by this script)');
  L.push('// generated     : ' + generated);
  L.push('// pending ops   : ' + ops.length);
  L.push('//');
  L.push('// This script is a proposal. Agent Builder does not execute it and');
  L.push('// never writes to Mongo. Applying it is a separate, deliberate act.');
  L.push('');

  L.push('// ---- 1. DRY RUN ----------------------------------------------------');
  L.push('// Default is true. Nothing is written until a human flips this by hand');
  L.push('// and reads the printed op list first.');
  L.push('const DRY_RUN = true;');
  L.push('');

  L.push('// ---- 2. ANCHORS ----------------------------------------------------');
  L.push('// Every update matches on agentId AND version AND the state name sitting');
  L.push('// at the index being written. If the states array was reordered after');
  L.push('// this script was generated, the anchor misses and that op is skipped');
  L.push('// rather than writing into the wrong state.');
  L.push('//');
  if (ops.length === 0) {
    L.push('//   (no pending ops, nothing is anchored)');
  } else {
    ops.forEach((op, i) => {
      L.push(
        '//   ops[' + i + ']  states.' + op.stateIndex +
        '  ' + op.stateName + '  ->  $set ' + op.path
      );
    });
  }
  L.push('');

  L.push('// ---- 3. OPS --------------------------------------------------------');
  L.push('const OPS = [');
  ops.forEach((op) => {
    L.push('  {');
    L.push('    // ' + op.label);
    L.push('    anchor: {');
    L.push("      agentId: '" + agent.agentId + "',");
    L.push('      version: ' + agent.version + ',');
    L.push("      'states." + op.stateIndex + ".name': '" + op.stateName + "'");
    L.push('    },');
    L.push('    set: { ' + JSON.stringify(op.path) + ': ' + JSON.stringify(op.value) + ' }');
    L.push('  },');
  });
  L.push('];');
  L.push('');

  L.push('// ---- 4. IDEMPOTENCE ------------------------------------------------');
  L.push('// Every op is an absolute $set. There is no $push, no $inc, no $addToSet,');
  L.push('// so applying the same value twice produces the same document. On a');
  L.push('// second run the version anchor no longer matches (it has moved to ' + nextVersion + '),');
  L.push('// so matchedCount is 0 and modifiedCount is 0. Re-running is safe and');
  L.push('// is the expected way to confirm the migration already landed.');
  L.push('');

  L.push('// ---- 5. APPLY ------------------------------------------------------');
  L.push('let matched = 0;');
  L.push('let modified = 0;');
  L.push('for (let i = 0; i < OPS.length; i++) {');
  L.push('  const op = OPS[i];');
  L.push('  if (DRY_RUN) {');
  L.push("    print('[DRY RUN] ops[' + i + '] ' + JSON.stringify(op.anchor) + ' $set ' + JSON.stringify(op.set));");
  L.push('    continue;');
  L.push('  }');
  L.push('  const res = db.unitedDomainAgents.updateOne(op.anchor, { $set: op.set });');
  L.push('  matched += res.matchedCount;');
  L.push('  modified += res.modifiedCount;');
  L.push('}');
  L.push("print('matched ' + matched + ' modified ' + modified + ' of ' + OPS.length);");
  L.push('');

  L.push('// ---- 6. VERSION ----------------------------------------------------');
  L.push('// Bump the document version once, last, and only if every op matched.');
  L.push('// activeVersion is deliberately absent from this $set. It stays at ' + agent.activeVersion + '.');
  L.push('// Promoting ' + nextVersion + ' to active is a separate human decision made in the');
  L.push('// console, after the eval harness has run against it.');
  L.push('if (!DRY_RUN && matched === OPS.length && OPS.length > 0) {');
  L.push('  db.unitedDomainAgents.updateOne(');
  L.push("    { agentId: '" + agent.agentId + "', version: " + agent.version + ' },');
  L.push('    { $set: { version: ' + nextVersion + ', updatedAt: new Date() } }');
  L.push('  );');
  L.push('}');
  L.push('');

  L.push('// ---- 7. S3 RECONCILE -----------------------------------------------');
  L.push('// Mongo is not the only copy. agents/' + agent.agentId + '/spec.md in S3 still');
  L.push('// holds version ' + agent.version + '. After this migration applies, regenerate spec.md');
  L.push('// from the migrated document and POST /api/internal/agents/' + agent.agentId + '/sync');
  L.push('// so S3, Mongo and the router candidate set agree.');
  L.push('//');
  L.push('// Skip the reconcile and the next sync reads the stale spec.md and');
  L.push('// overwrites everything this script just did.');

  return L.join('\n');
};

// ---------------------------------------------------------------------------
// Left rail
// ---------------------------------------------------------------------------

const AgentRow = ({ agent, active, issueCount, stateCount, lean, onClick }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      width: '100%',
      textAlign: 'left',
      cursor: 'pointer',
      background: active ? 'rgba(255,255,255,0.04)' : 'transparent',
      border: 'none',
      borderLeft: active ? `2px solid ${THEME.mint}` : '2px solid transparent',
      px: 1.75,
      py: 1.25,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1,
      '&:hover': { background: 'rgba(255,255,255,0.03)' },
    }}
  >
    <Box sx={{ fontSize: '18px', lineHeight: 1.2, flexShrink: 0, opacity: lean ? 0.45 : 1 }}>
      {agent.iconHint}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            color: lean ? THEME.textMuted : THEME.textPrimary,
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {agent.displayName}
        </Typography>
        {issueCount > 0 ? (
          <Badge tone="pink">
            {issueCount} {issueCount === 1 ? 'issue' : 'issues'}
          </Badge>
        ) : (
          <Badge tone="mint">clean</Badge>
        )}
      </Box>
      {/* The count lives in the badge above, so it is not repeated here. */}
      <Typography sx={{ fontSize: '11px', color: lean ? THEME.textFaint : THEME.textMuted }}>
        <Box component="span" sx={{ fontFamily: 'monospace', color: THEME.textFaint }}>
          v{agent.version}
        </Box>
        {'  '}
        {stateCount} {stateCount === 1 ? 'state' : 'states'}
      </Typography>
      {lean && (
        <Typography sx={{ fontSize: '10px', color: THEME.textFaint, fontStyle: 'italic', lineHeight: 1.4, mt: 0.25 }}>
          detail loads when the backend endpoint exists
        </Typography>
      )}
    </Box>
  </Box>
);

// ---------------------------------------------------------------------------
// Palette chips (drag sources)
// ---------------------------------------------------------------------------

const PaletteChip = ({ kind, value, label, tone, title, onDragStart, onDragEnd }) => (
  <Tooltip title={title || ''} placement="top" {...passThroughTip}>
    <Box
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'copy';
        e.dataTransfer.setData(DND_MIME, JSON.stringify({ kind, value }));
        e.dataTransfer.setData('text/plain', value);
        if (onDragStart) onDragStart({ kind, value });
      }}
      onDragEnd={() => onDragEnd && onDragEnd()}
      sx={{
        cursor: 'grab',
        userSelect: 'none',
        background: 'rgba(255,255,255,0.06)',
        border: `1px solid ${THEME.border}`,
        borderRadius: '6px',
        px: 1,
        py: 0.5,
        fontSize: '11px',
        fontFamily: 'monospace',
        color: tone || THEME.textSecondary,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        whiteSpace: 'nowrap',
        '&:hover': { borderColor: THEME.mint, color: THEME.mint },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      {label || value}
    </Box>
  </Tooltip>
);

// Collapsible drawer in the right rail. Collapsed by default so the state rail
// keeps the eye. The identifier stays PaletteSection; the rendered titles are
// plain "Tools" and "Artifacts" and the word palette appears nowhere on screen.
const PaletteSection = ({ title, caption, count, open, onToggle, IconComponent, tip, children }) => (
  <Box sx={{ borderBottom: `1px solid ${THEME.border}`, flexShrink: 0 }}>
    <Box
      component="button"
      onClick={onToggle}
      sx={{
        width: '100%',
        textAlign: 'left',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        px: 2,
        py: 1.25,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        '&:hover': { background: 'rgba(255,255,255,0.02)' },
      }}
    >
      {IconComponent && (
        <IconComponent sx={{ fontSize: 15, color: THEME.textMuted, flexShrink: 0, mt: '1px' }} />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: THEME.textFaint,
            }}
          >
            {title}
          </Typography>
          {tip && <HintIcon text={tip} />}
        </Box>
        {caption && (
          <Typography sx={{ fontSize: '11px', color: THEME.textMuted, lineHeight: 1.5, mt: 0.25 }}>
            {caption}
          </Typography>
        )}
      </Box>
      <Typography sx={{ fontSize: '11px', color: THEME.textMuted, flexShrink: 0 }}>{count}</Typography>
      <ExpandMoreIcon
        sx={{
          fontSize: 18,
          color: THEME.textMuted,
          flexShrink: 0,
          transition: 'transform 0.2s',
          transform: open ? 'rotate(180deg)' : 'none',
        }}
      />
    </Box>
    {open && (
      <Box sx={{ px: 2, pb: 1.5, maxHeight: 200, overflowY: 'auto', ...scrollSx }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>{children}</Box>
      </Box>
    )}
  </Box>
);

// ---------------------------------------------------------------------------
// State chip (drag source and drop target)
// ---------------------------------------------------------------------------

const IssuePip = ({ issue }) => (
  <Tooltip
    title={`${issue.code} (${issue.severity}): ${issue.why}`}
    placement="bottom"
    {...passThroughTip}
  >
    <Box
      sx={{
        width: 7,
        height: 7,
        borderRadius: '50%',
        background: SEVERITY_COLOR[issue.severity] || THEME.textMuted,
        cursor: 'help',
        flexShrink: 0,
      }}
    />
  </Tooltip>
);

const StateChip = ({
  state,
  index,
  selected,
  issues,
  dragOver,
  dragActive,
  validTarget,
  onSelect,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
}) => {
  // During a drag every valid target shows the dashed outline; the one under
  // the pointer flips to mint plus mintFaint.
  const armed = dragActive && validTarget;
  const border = dragOver
    ? `1px dashed ${THEME.mint}`
    : armed
    ? `1px dashed ${THEME.borderStrong}`
    : selected
    ? `1px solid ${THEME.mint}`
    : `1px solid ${THEME.border}`;
  const background = dragOver || selected ? THEME.mintFaint : 'rgba(255,255,255,0.02)';

  return (
    <Box
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={onSelect}
      sx={{
        cursor: 'grab',
        userSelect: 'none',
        flex: '0 0 auto',
        minWidth: 200,
        p: 1.75,
        borderRadius: '10px',
        background,
        border,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        '&:hover': { borderColor: THEME.mint },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: THEME.mintDim,
            color: THEME.mint,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {index + 1}
        </Box>
        {/* label first when the state has one, identifier underneath. A state
            with no label keeps today's rendering exactly: identifier as the
            primary text, nothing beneath it. */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '13px',
              fontFamily: state.label ? '"IBM Plex Sans", -apple-system, sans-serif' : 'monospace',
              fontWeight: 600,
              color: selected ? THEME.mint : THEME.textPrimary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {state.label || state.name}
          </Typography>
          {state.label && (
            <Typography
              sx={{
                fontSize: '10px',
                fontFamily: 'monospace',
                color: THEME.textFaint,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {state.name}
            </Typography>
          )}
        </Box>
        {state.haltTurn && (
          <Tooltip title="Halts the turn and waits for the user" placement="top" {...passThroughTip}>
            <PauseCircleOutlineIcon sx={{ fontSize: 16, color: THEME.amber, flexShrink: 0 }} />
          </Tooltip>
        )}
      </Box>

      <Typography
        sx={{
          fontSize: '11px',
          fontFamily: 'monospace',
          color: state.artifact ? THEME.textFaint : 'rgba(198,61,87,0.7)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {state.artifact || 'no artifact'}
      </Typography>

      <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>
        {(state.tools || []).length} {(state.tools || []).length === 1 ? 'tool' : 'tools'}
        {'  '}
        {(state.allowedExits || []).length} {(state.allowedExits || []).length === 1 ? 'exit' : 'exits'}
      </Typography>

      <Box sx={{ display: 'flex', gap: '4px', minHeight: 7 }}>
        {issues.map((f, i) => (
          <IssuePip key={`${f.code}-${i}`} issue={f} />
        ))}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Inspector rows
// ---------------------------------------------------------------------------

const RemovableRow = ({ label, sublabel, tone, onRemove, removeTitle }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1,
      py: 0.75,
      borderBottom: `1px solid ${THEME.border}`,
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: '12px', fontFamily: 'monospace', color: tone || THEME.textPrimary }}>
        {label}
      </Typography>
      {sublabel && (
        <Typography sx={{ fontSize: '10px', color: THEME.textMuted, mt: 0.25, lineHeight: 1.4 }}>
          {sublabel}
        </Typography>
      )}
    </Box>
    <Box
      component="button"
      title={removeTitle || 'Remove'}
      onClick={onRemove}
      sx={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: THEME.textMuted,
        p: 0,
        flexShrink: 0,
        '&:hover': { color: THEME.pink },
      }}
    >
      <CloseIcon sx={{ fontSize: 14 }} />
    </Box>
  </Box>
);

const MemoryChips = ({ label, keys }) => (
  <Box sx={{ mb: 1.25 }}>
    <Typography
      sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}
    >
      {label}
    </Typography>
    {(keys || []).length === 0 ? (
      <Typography sx={{ fontSize: '11px', color: THEME.textFaint, fontStyle: 'italic' }}>none</Typography>
    ) : (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {keys.map((k) => (
          <Box
            key={k}
            sx={{
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${THEME.border}`,
              borderRadius: '6px',
              px: 0.75,
              py: '2px',
              fontSize: '10px',
              fontFamily: 'monospace',
              color: THEME.textSecondary,
            }}
          >
            {k}
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

// ---------------------------------------------------------------------------
// Migration tray
// ---------------------------------------------------------------------------
//
// Renders as "Update script". MigrationTray and buildMigrationScript keep the
// backend word for the same reason the header button does; see the note there.

const MigrationTray = ({ open, onClose, agent, ops, onClear }) => {
  const [copied, setCopied] = useState(false);
  const script = useMemo(() => (agent ? buildMigrationScript(agent, ops) : ''), [agent, ops]);

  const copy = () => {
    // Clipboard API is not available on an insecure origin. Fall back to the
    // textarea trick rather than failing silently.
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(script).then(done, () => {});
      return;
    }
    const ta = document.createElement('textarea');
    ta.value = script;
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      done();
    } catch (err) {
      // Nothing to do. The script is on screen and selectable.
    }
    document.body.removeChild(ta);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Update script"
      subtitle={agent ? `${agent.agentId}  v${agent.version} to v${(agent.version || 1) + 1}` : ''}
      size="lg"
      footer={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {copied && <Typography sx={{ fontSize: '11px', color: THEME.mint }}>copied</Typography>}
          <Button variant="outlined" sx={outlinedBtnSx} onClick={onClose}>
            Close
          </Button>
          <Button variant="contained" startIcon={<ContentCopyIcon />} sx={mintBtnSx} onClick={copy}>
            Copy
          </Button>
        </Box>
      }
    >
      <Box
        sx={{
          p: 1.5,
          mb: 2,
          borderRadius: '8px',
          background: THEME.mintFaint,
          border: `1px solid ${THEME.mint}`,
          display: 'flex',
          gap: 1,
          alignItems: 'flex-start',
        }}
      >
        <ArticleIcon sx={{ color: THEME.mint, fontSize: 18, flexShrink: 0, mt: '1px' }} />
        <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.6 }}>
          This page proposes changes. It never writes them. Copy the script, read it, and run it
          yourself against the collection when you are satisfied. DRY_RUN is true by default.
        </Typography>
      </Box>

      {/* pending ops, in plain English, above the script they become */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography
          sx={{ fontSize: '10px', color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}
        >
          Pending ops ({ops.length})
        </Typography>
        <HintIcon text={TIP.pendingOps} />
        <Box sx={{ flex: 1 }} />
        {ops.length > 0 && (
          <Box
            component="button"
            onClick={onClear}
            sx={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: THEME.mint,
              fontSize: '11px',
              fontWeight: 600,
              p: 0,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Clear
          </Box>
        )}
      </Box>

      {ops.length === 0 ? (
        <Typography sx={{ fontSize: '12px', color: THEME.textMuted, fontStyle: 'italic', mb: 2, lineHeight: 1.6 }}>
          Nothing staged. The script below is the header and the reconcile note only. Drag a tool, a
          state or an artifact onto a state chip, or edit a prompt, and the ops appear here.
        </Typography>
      ) : (
        <Box sx={{ mb: 2 }}>
          {ops.map((op, i) => (
            <Box
              key={op.id}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                py: 1,
                borderBottom: i < ops.length - 1 ? `1px solid ${THEME.border}` : 'none',
              }}
            >
              <Box sx={{ mt: '5px' }}>
                <StatusDot color={THEME.amber} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '12px', color: THEME.textPrimary }}>{op.label}</Typography>
                <Typography sx={{ fontSize: '10px', color: THEME.textFaint, fontFamily: 'monospace' }}>
                  $set {op.path}
                </Typography>
              </Box>
              <Badge tone="amber">staged</Badge>
            </Box>
          ))}
        </Box>
      )}

      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          borderRadius: '8px',
          background: THEME.bgDeep,
          border: `1px solid ${THEME.border}`,
          fontFamily: 'monospace',
          fontSize: '11px',
          lineHeight: 1.6,
          color: THEME.textSecondary,
          overflowX: 'auto',
          whiteSpace: 'pre',
          ...scrollSx,
          '&::-webkit-scrollbar': { width: '6px', height: '6px' },
        }}
      >
        {script}
      </Box>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const AgentBuilderPage = () => {
  // WIRE: GET /api/internal/agents (exists, lean only)
  //   Seeds this list. The lean payload has no states, so a row hydrated from
  //   it can fill the rail but the center and right columns stay empty until
  //   the /full call below lands. Those rows are dimmed and say so.
  // WIRE: GET /api/internal/agents/:id/full (must be built, T1)
  //   Hydrates agent.states for the selected agent. Everything the center and
  //   right columns render comes from that call.
  const [agents, setAgents] = useState(AGENTS);
  const [selectedAgentId, setSelectedAgentId] = useState('travel_booking_agent');
  const [selectedStateName, setSelectedStateName] = useState('PAYMENT_CONFIRM');
  const [ops, setOps] = useState([]);
  const [trayOpen, setTrayOpen] = useState(false);

  // Drag bookkeeping. `drag` is the payload currently in flight, so every valid
  // target can arm itself and the hint line can say what a drop would do.
  const [drag, setDrag] = useState(null);
  const [dragOverState, setDragOverState] = useState(null);

  // Both palettes start closed. The state rail is the point of the page.
  const [toolsOpen, setToolsOpen] = useState(false);
  const [artifactsOpen, setArtifactsOpen] = useState(false);

  // Prompt edits are staged on blur, not on keystroke. Appending an op per
  // character would flood the ops list with a hundred writes of the same path.
  const [promptDraft, setPromptDraft] = useState(null);
  const promptBaselineRef = useRef('');

  const agent = agents.find((a) => a.agentId === selectedAgentId) || agents[0];
  const states = agent.states || [];

  const { byState, all: allIssues } = useMemo(
    // WIRE: GET /api/internal/agents/tools (must be built, T1)
    //   TOOL_CATALOG is the only source of dispatchable, money and tier. Swap
    //   it for the live catalog and the UNDISPATCHABLE and UNTIERED_MONEY
    //   rules start reflecting reality instead of fixtures.
    () => issuesForAgent(agent, TOOL_CATALOG, ARTIFACT_CATALOG),
    [agent]
  );

  const selectedState = states.find((s) => s.name === selectedStateName) || states[0] || null;
  const selectedIssues = selectedState ? byState[selectedState.name] || [] : [];

  // -------------------------------------------------------------------------
  // Mutations. Each one edits the local working copy and appends a pending op.
  // Nothing leaves this component.
  // -------------------------------------------------------------------------

  const patchState = (stateName, patch) => {
    setAgents((prev) =>
      prev.map((a) =>
        a.agentId !== agent.agentId
          ? a
          : { ...a, states: a.states.map((s) => (s.name === stateName ? { ...s, ...patch } : s)) }
      )
    );
  };

  const appendOp = (op) => setOps((prev) => [...prev, { ...op, id: nextOpId() }]);

  const opBase = (stateName, field) => {
    const stateIndex = states.findIndex((s) => s.name === stateName);
    return { agentId: agent.agentId, stateName, stateIndex, path: `states.${stateIndex}.${field}` };
  };

  const addTool = (stateName, toolName) => {
    const state = states.find((s) => s.name === stateName);
    if (!state || (state.tools || []).includes(toolName)) return;
    const value = [...(state.tools || []), toolName];
    patchState(stateName, { tools: value });
    appendOp({ ...opBase(stateName, 'tools'), value, label: `add tool ${toolName} to ${stateName}` });
  };

  const removeTool = (stateName, toolName) => {
    const state = states.find((s) => s.name === stateName);
    if (!state) return;
    const value = (state.tools || []).filter((t) => t !== toolName);
    patchState(stateName, { tools: value });
    appendOp({ ...opBase(stateName, 'tools'), value, label: `remove tool ${toolName} from ${stateName}` });
  };

  const addExit = (fromStateName, toStateName) => {
    if (fromStateName === toStateName) return;
    const state = states.find((s) => s.name === fromStateName);
    if (!state || (state.allowedExits || []).includes(toStateName)) return;
    const value = [...(state.allowedExits || []), toStateName];
    patchState(fromStateName, { allowedExits: value });
    appendOp({
      ...opBase(fromStateName, 'allowedExits'),
      value,
      label: `add exit ${fromStateName} to ${toStateName}`,
    });
  };

  const removeExit = (stateName, exitName) => {
    const state = states.find((s) => s.name === stateName);
    if (!state) return;
    const value = (state.allowedExits || []).filter((x) => x !== exitName);
    patchState(stateName, { allowedExits: value });
    appendOp({ ...opBase(stateName, 'allowedExits'), value, label: `remove exit ${stateName} to ${exitName}` });
  };

  const bindArtifact = (stateName, artifactName) => {
    const state = states.find((s) => s.name === stateName);
    if (!state || state.artifact === artifactName) return;
    patchState(stateName, { artifact: artifactName });
    appendOp({
      ...opBase(stateName, 'artifact'),
      value: artifactName,
      label: `bind artifact ${artifactName} to ${stateName}`,
    });
  };

  const commitPrompt = (stateName) => {
    if (promptDraft === null) return;
    const next = promptDraft;
    setPromptDraft(null);
    if (next === promptBaselineRef.current) return;
    patchState(stateName, { prompt: next });
    appendOp({ ...opBase(stateName, 'prompt'), value: next, label: `rewrite prompt on ${stateName}` });
  };

  // -------------------------------------------------------------------------
  // Drag handling
  // -------------------------------------------------------------------------

  const endDrag = () => {
    setDrag(null);
    setDragOverState(null);
  };

  // A chip is a valid target for everything except a state dropped on itself.
  const isValidTarget = (stateName) => !!drag && !(drag.kind === 'state' && drag.value === stateName);

  const handleDropOnState = (e, stateName) => {
    e.preventDefault();
    const payload = readDrag(e);
    endDrag();
    if (!payload) return;
    if (payload.kind === 'tool') addTool(stateName, payload.value);
    else if (payload.kind === 'artifact') bindArtifact(stateName, payload.value);
    else if (payload.kind === 'state') addExit(payload.value, stateName);
  };

  // One line under the rail, saying exactly what a drop would do right now.
  const dragHint = () => {
    if (!drag) return null;
    const target = dragOverState;
    const st = target ? states.find((s) => s.name === target) : null;

    if (drag.kind === 'tool') {
      if (!st) return `Drop ${drag.value} onto a state to add it`;
      if ((st.tools || []).includes(drag.value)) return `${drag.value} is already on ${target}`;
      return `Drop to add ${drag.value} to ${target}`;
    }
    if (drag.kind === 'artifact') {
      if (!st) return `Drop ${drag.value} onto a state to bind its card`;
      if (st.artifact === drag.value) return `${target} is already bound to ${drag.value}`;
      return `Drop to bind ${drag.value} to ${target}`;
    }
    // kind === 'state'
    if (!st) return `Drop ${drag.value} onto another state to add an exit`;
    if (target === drag.value) return 'A state cannot exit to itself';
    const source = states.find((s) => s.name === drag.value);
    if (source && (source.allowedExits || []).includes(target)) {
      return `${drag.value} already exits to ${target}`;
    }
    return `Drop to allow ${drag.value} to exit to ${target}`;
  };

  const selectState = (name) => {
    setPromptDraft(null);
    setSelectedStateName(name);
  };

  const selectAgent = (id) => {
    const next = agents.find((a) => a.agentId === id);
    setPromptDraft(null);
    setSelectedAgentId(id);
    setSelectedStateName(next && next.states && next.states[0] ? next.states[0].name : '');
  };

  const promptValue = promptDraft !== null ? promptDraft : (selectedState ? selectedState.prompt : '');

  const isLean = (a) => !(a.states && a.states.length > 0);

  const railIssueCount = (a) => (isLean(a)
    ? a.issueCount || 0
    : issuesForAgent(a, TOOL_CATALOG, ARTIFACT_CATALOG).all.length);

  const railStateCount = (a) => (isLean(a) ? a.stateCount || 0 : a.states.length);

  const hint = dragHint();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 0px)',
        background: THEME.bg,
        color: THEME.textPrimary,
        fontFamily: '"IBM Plex Sans", -apple-system, sans-serif',
      }}
    >
      {/* header bar */}
      <Box
        sx={{
          borderBottom: `1px solid ${THEME.border}`,
          px: 3,
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            Agent Builder
          </Typography>
          <Typography variant="caption" sx={{ color: THEME.textMuted }}>
            States, tools, exits and artifacts for the unitedDomainAgents fleet. Proposes update scripts, never writes them.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexShrink: 0 }}>
          <Badge tone={ops.length > 0 ? 'amber' : 'muted'}>
            {ops.length} pending {ops.length === 1 ? 'op' : 'ops'}
          </Badge>
          {/* The label says "Generate Script" but the function behind it is
              buildMigrationScript and the endpoint is .../migration. That is
              deliberate, not drift: "script" is the plain-English face of the
              thing, "migration" is what ops and the backend call it, and the
              T6 route name should not follow a UI wording change silently.
              Same divergence on MigrationTray, which renders as "Update
              script". If the endpoint is ever renamed, these labels are
              already correct and only the identifiers move. */}
          {/* WIRE: POST /api/admin/agent-builder/migration (later, T6)
              Today this opens a local tray. Later it posts `ops` and renders
              the server generated script. */}
          <Button
            variant="contained"
            startIcon={<DescriptionOutlinedIcon />}
            sx={mintBtnSx}
            onClick={() => setTrayOpen(true)}
          >
            Generate Script
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* ------------------------------------------------------------- */}
        {/* left rail: agents                                             */}
        {/* ------------------------------------------------------------- */}
        <Box
          sx={{
            width: 240,
            flexShrink: 0,
            borderRight: `1px solid ${THEME.border}`,
            background: THEME.bgDeep,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 1.75,
              py: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: `1px solid ${THEME.border}`,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: THEME.textFaint,
                }}
              >
                Agents
              </Typography>
              <HintIcon text={TIP.agentList} />
            </Box>
            <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>{agents.length}</Typography>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', ...scrollSx }}>
            {agents.map((a) => (
              <AgentRow
                key={a.agentId}
                agent={a}
                active={a.agentId === selectedAgentId}
                issueCount={railIssueCount(a)}
                stateCount={railStateCount(a)}
                lean={isLean(a)}
                onClick={() => selectAgent(a.agentId)}
              />
            ))}
          </Box>
        </Box>

        {/* ------------------------------------------------------------- */}
        {/* center: the state rail, and nothing else                      */}
        {/* ------------------------------------------------------------- */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3, ...scrollSx }}>
          {/* agent header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
            <Box sx={{ fontSize: '28px', lineHeight: 1, flexShrink: 0 }}>{agent.iconHint}</Box>
            <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>{agent.displayName}</Typography>
            <Badge tone="muted">v{agent.version}</Badge>
            <Badge tone="blue">active v{agent.activeVersion}</Badge>
            {allIssues.length > 0 && (
              <Badge tone="pink">
                {allIssues.length} {allIssues.length === 1 ? 'issue' : 'issues'}
              </Badge>
            )}
          </Box>
          <Typography sx={{ fontSize: '11px', color: THEME.textFaint, fontFamily: 'monospace', mb: 0.5 }}>
            {agent.agentId}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: THEME.textMuted, mb: 2.5 }}>{agent.tagline}</Typography>

          {states.length === 0 ? (
            <Card>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <WarningAmberIcon sx={{ color: THEME.amber, fontSize: 20, flexShrink: 0, mt: '1px' }} />
                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 0.5 }}>
                    States not loaded for this agent
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.6 }}>
                    The lean agent list carries {railStateCount(agent)} states and {railIssueCount(agent)} issues
                    for this agent as counts only. The rail, the inspector and the issue rules all need the full
                    document, which comes from GET /api/internal/agents/{agent.agentId}/full. That endpoint has
                    not been built yet.
                  </Typography>
                </Box>
              </Box>
            </Card>
          ) : (
            <Card>
              <SectionHeader
                n="S"
                label="States"
                sublabel={`${states.length} in document order, initial state ${agent.initialState}`}
                IconComponent={LayersIcon}
                labelTip={TIP.stateRail}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: 1.25 }}>
                {states.map((s, i) => (
                  <StateChip
                    key={s.name}
                    state={s}
                    index={i}
                    selected={selectedState && selectedState.name === s.name}
                    issues={byState[s.name] || []}
                    dragOver={dragOverState === s.name}
                    dragActive={!!drag}
                    validTarget={isValidTarget(s.name)}
                    onSelect={() => selectState(s.name)}
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'copy';
                      e.dataTransfer.setData(DND_MIME, JSON.stringify({ kind: 'state', value: s.name }));
                      e.dataTransfer.setData('text/plain', s.name);
                      setDrag({ kind: 'state', value: s.name });
                    }}
                    onDragEnd={endDrag}
                    onDragOver={(e) => {
                      if (!isBuilderDrag(e)) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'copy';
                      if (dragOverState !== s.name) setDragOverState(s.name);
                    }}
                    onDragLeave={() => setDragOverState((cur) => (cur === s.name ? null : cur))}
                    onDrop={(e) => handleDropOnState(e, s.name)}
                  />
                ))}
              </Box>

              {/* one line under the rail: the static legend, or the live drop hint */}
              {hint ? (
                <Typography sx={{ fontSize: '12px', color: THEME.mint, fontWeight: 600, mt: 1.5, lineHeight: 1.6 }}>
                  {hint}
                </Typography>
              ) : (
                <Typography sx={{ fontSize: '11px', color: THEME.textFaint, mt: 1.5, lineHeight: 1.6 }}>
                  Drag a step onto another to add an exit, or open Tools or Artifacts on the right and drag one
                  onto a step. The pause marker means the step halts and waits for the user. Pips are issues,
                  red for error and amber for drift.
                </Typography>
              )}
            </Card>
          )}
        </Box>

        {/* ------------------------------------------------------------- */}
        {/* right rail: palettes, then the inspector                      */}
        {/* ------------------------------------------------------------- */}
        <Box
          sx={{
            width: 360,
            flexShrink: 0,
            background: THEME.bgDeep,
            borderLeft: `1px solid ${THEME.border}`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <PaletteSection
            title="Tools"
            caption="Drag one onto a step to allow the agent to do it there."
            count={TOOL_CATALOG.length}
            open={toolsOpen}
            onToggle={() => setToolsOpen((o) => !o)}
            IconComponent={BuildIcon}
            tip={TIP.tools}
          >
            {TOOL_CATALOG.map((t) => (
              <PaletteChip
                key={t.name}
                kind="tool"
                value={t.name}
                tone={t.dispatchable === false ? THEME.pink : t.money ? THEME.amber : THEME.textSecondary}
                title={
                  t.description +
                  (t.dispatchable === false ? '  Not dispatchable.' : '') +
                  (t.money ? `  Moves money, tier ${t.tier || 'none'}.` : '')
                }
                onDragStart={setDrag}
                onDragEnd={endDrag}
              />
            ))}
          </PaletteSection>

          <PaletteSection
            title="Artifacts"
            caption="Drag one onto a step to show that card on the user's screen at that step."
            count={ARTIFACT_CATALOG.length}
            open={artifactsOpen}
            onToggle={() => setArtifactsOpen((o) => !o)}
            IconComponent={ArticleIcon}
            tip={TIP.artifacts}
          >
            {ARTIFACT_CATALOG.map((a) => (
              <PaletteChip
                key={a.name}
                kind="artifact"
                value={a.name}
                tone={a.registered === false ? THEME.pink : THEME.textSecondary}
                title={a.description + (a.registered === false ? '  Not registered, has no card renderer.' : '')}
                onDragStart={setDrag}
                onDragEnd={endDrag}
              />
            ))}
          </PaletteSection>

          {/* inspector */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${THEME.border}`,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              flexShrink: 0,
            }}
          >
            <AccountTreeIcon sx={{ fontSize: 16, color: THEME.textMuted, mt: '2px' }} />
            {/* Both names, always: the plain-English one to read and the
                identifier to match against the document. */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  fontFamily: selectedState && selectedState.label
                    ? '"IBM Plex Sans", -apple-system, sans-serif'
                    : 'monospace',
                  color: THEME.textPrimary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedState ? selectedState.label || selectedState.name : 'No step selected'}
              </Typography>
              {selectedState && selectedState.label && (
                <Typography
                  sx={{
                    fontSize: '10px',
                    fontFamily: 'monospace',
                    color: THEME.textFaint,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {selectedState.name}
                </Typography>
              )}
            </Box>
            {selectedState && selectedState.haltTurn && <Badge tone="amber">halts</Badge>}
          </Box>

          {!selectedState ? (
            <Box sx={{ p: 2 }}>
              <Typography sx={{ fontSize: '12px', color: THEME.textMuted, fontStyle: 'italic', lineHeight: 1.6 }}>
                This agent has no states loaded, so there is nothing to inspect.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2, ...scrollSx }}>
              {/* issues, explained */}
              <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '10px',
                      color: THEME.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                  >
                    Issues ({selectedIssues.length})
                  </Typography>
                  <HintIcon text={TIP.issues} />
                </Box>
                {selectedIssues.length === 0 ? (
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: '8px',
                      background: THEME.mintFaint,
                      border: `1px solid ${THEME.mint}`,
                    }}
                  >
                    <Typography sx={{ fontSize: '11px', color: THEME.mint, lineHeight: 1.5 }}>
                      No rule fires on this state.
                    </Typography>
                  </Box>
                ) : (
                  selectedIssues.map((f) => (
                    <Box
                      key={f.code}
                      sx={{
                        p: 1.25,
                        mb: 1,
                        borderRadius: '8px',
                        background:
                          f.severity === 'error' ? 'rgba(198,61,87,0.10)' : 'rgba(251,191,36,0.10)',
                        border: `1px solid ${SEVERITY_COLOR[f.severity]}`,
                        display: 'flex',
                        gap: 1,
                        alignItems: 'flex-start',
                      }}
                    >
                      <WarningAmberIcon
                        sx={{ color: SEVERITY_COLOR[f.severity], fontSize: 15, flexShrink: 0, mt: '1px' }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <Typography
                            sx={{
                              fontSize: '11px',
                              fontWeight: 700,
                              fontFamily: 'monospace',
                              color: SEVERITY_COLOR[f.severity],
                              letterSpacing: '0.04em',
                            }}
                          >
                            {f.code}
                          </Typography>
                          <Badge tone={f.severity === 'error' ? 'pink' : 'amber'}>{f.severity}</Badge>
                        </Box>
                        <Typography sx={{ fontSize: '11px', color: THEME.textSecondary, lineHeight: 1.6, mt: 0.25 }}>
                          {f.why}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>

              {/* prompt */}
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: '10px',
                    color: THEME.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    mb: 0.5,
                  }}
                >
                  Prompt
                </Typography>
                <TextField
                  multiline
                  fullWidth
                  minRows={6}
                  maxRows={14}
                  placeholder="No prompt on this state. The model will improvise."
                  value={promptValue || ''}
                  onFocus={() => {
                    promptBaselineRef.current = selectedState.prompt || '';
                    setPromptDraft(selectedState.prompt || '');
                  }}
                  onChange={(e) => setPromptDraft(e.target.value)}
                  onBlur={() => commitPrompt(selectedState.name)}
                  InputProps={{
                    sx: {
                      fontSize: 12,
                      lineHeight: 1.6,
                      color: THEME.textPrimary,
                      '& fieldset': { borderColor: THEME.border },
                      '&:hover fieldset': { borderColor: THEME.borderStrong },
                    },
                  }}
                />
                <Typography sx={{ fontSize: '10px', color: THEME.textFaint, mt: 0.5 }}>
                  Staged as one op when the field loses focus, not per keystroke.
                </Typography>
              </Box>

              {/* tools */}
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: '10px',
                    color: THEME.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    mb: 0.5,
                  }}
                >
                  Tools ({(selectedState.tools || []).length})
                </Typography>
                {(selectedState.tools || []).length === 0 ? (
                  <Typography sx={{ fontSize: '11px', color: THEME.textFaint, fontStyle: 'italic' }}>
                    none. Drag one from Tools above.
                  </Typography>
                ) : (
                  selectedState.tools.map((name) => {
                    const t = TOOL_CATALOG.find((x) => x.name === name);
                    const bits = [];
                    if (t && t.dispatchable === false) bits.push('not dispatchable');
                    if (t && t.money) bits.push(`money, tier ${t.tier || 'none'}`);
                    if (!t) bits.push('not in the tool catalog');
                    return (
                      <RemovableRow
                        key={name}
                        label={name}
                        sublabel={bits.join('  ')}
                        tone={t && (t.dispatchable === false || (t.money && !t.tier)) ? THEME.pink : THEME.textPrimary}
                        removeTitle={`Remove ${name}`}
                        onRemove={() => removeTool(selectedState.name, name)}
                      />
                    );
                  })
                )}
              </Box>

              {/* exits */}
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: '10px',
                    color: THEME.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    mb: 0.5,
                  }}
                >
                  Allowed exits ({(selectedState.allowedExits || []).length})
                </Typography>
                {(selectedState.allowedExits || []).length === 0 ? (
                  <Typography sx={{ fontSize: '11px', color: THEME.textFaint, fontStyle: 'italic' }}>
                    none. Drag this state chip onto another state to add one.
                  </Typography>
                ) : (
                  selectedState.allowedExits.map((exit) => {
                    const isState = states.some((s) => s.name === exit);
                    const isTerminal =
                      (agent.completedStates || []).includes(exit) || (agent.escalatedStates || []).includes(exit);
                    const sub = isState ? 'state' : isTerminal ? 'terminal label' : 'target does not exist';
                    return (
                      <RemovableRow
                        key={exit}
                        label={exit}
                        sublabel={sub}
                        tone={isState || isTerminal ? THEME.textPrimary : THEME.pink}
                        removeTitle={`Remove exit to ${exit}`}
                        onRemove={() => removeExit(selectedState.name, exit)}
                      />
                    );
                  })
                )}
              </Box>

              {/* memory contract, read only */}
              <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '10px',
                      color: THEME.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                  >
                    Memory contract
                  </Typography>
                  <HintIcon text={TIP.memoryContract} />
                  <Box sx={{ ml: 0.75 }}>
                    <Badge tone="muted">read only</Badge>
                  </Box>
                </Box>
                <MemoryChips label="Required entering" keys={selectedState.requiredEnteringMemory} />
                <MemoryChips label="Required exiting" keys={selectedState.requiredExitingMemory} />
                <Typography sx={{ fontSize: '10px', color: THEME.textFaint, lineHeight: 1.6 }}>
                  Narration tone {selectedState.narrationTone}, pill strategy {selectedState.pillStrategy}.
                </Typography>
              </Box>

              {/* test */}
              <Box sx={{ pt: 1, borderTop: `1px solid ${THEME.border}` }}>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled
                  startIcon={<ScienceIcon />}
                  title="needs the eval harness"
                  sx={{
                    ...outlinedBtnSx,
                    '&.Mui-disabled': { color: THEME.textFaint, borderColor: THEME.border },
                  }}
                >
                  Test
                </Button>
                <Typography sx={{ fontSize: '10px', color: THEME.textFaint, mt: 0.75, textAlign: 'center' }}>
                  needs the eval harness
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <MigrationTray
        open={trayOpen}
        onClose={() => setTrayOpen(false)}
        agent={agent}
        ops={ops}
        onClear={() => setOps([])}
      />
    </Box>
  );
};

export default AgentBuilderPage;
